/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useTheme } from "@emotion/react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Card, Grid, Stack, IconButton, Icon, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SetOutOfOfficeDaysModal from "components/OutOfOfficeDays/SetOutOfOfficeDaysModal";
import { getAPICall, postAPICall, deleteAPICall } from "lib/callApi";
import {
  COMPANY_HOLIDAYS,
  TOGGLE_COMPANY_HOLIDAYS,
  FETCH_ALL_OUT_OF_OFFICE_DAYS,
  DELETE_OUT_OF_OFFICE_DAYS,
} from "lib/constants";
import Loader from "utils/Loader";
import TooltipLight from "utils/TooltipLight";
import CompanyHolidaysUserTable from "./CompanyHolidaysUserTable";

const CompanyHolidays = ({ viewAsUserHoliday = false }) => {
  const theme = useTheme();
  const calendarRef = useRef(null);
  const loggedInUser = useSelector((state) => state.loginUser.loginUserState);

  const [pageLoading, setPageLoading] = useState(true);
  const [companyHolidaysList, setCompanyHolidaysList] = useState([]);
  const [isOutOfOfficeDaysModalOpen, setIsOutOfOfficeDaysModalOpen] = useState(false);
  const [calendarHeight, setCalendarHeight] = useState(0);
  const [selectedDates, setSelectedDates] = useState();
  const [isEditing, setIsEditing] = useState(false);

  const calendarEvents = useMemo(() => {
    if (!companyHolidaysList?.length) return [];

    return companyHolidaysList
      .map((companyHoliday) => {
        if (
          viewAsUserHoliday &&
          companyHoliday?.FK_user_companies_links_id !== loggedInUser?.userCompanyLinkId
        )
          return null;
        return {
          title: companyHoliday.name,
          start: moment(companyHoliday.days[0].day).startOf("d").format(),
          ...(companyHoliday.days.length > 1
            ? {
                end: moment(companyHoliday.days[companyHoliday.days.length - 1].day)
                  .endOf("d")
                  .format(),
                allDay: false,
              }
            : {}),
          ...(companyHoliday.isCustom ? { backgroundColor: theme.palette.warning.main } : {}),
          isCustom: companyHoliday.isCustom,
          key: companyHoliday.key,
        };
      })
      .filter((event) => event);
  }, [companyHolidaysList, viewAsUserHoliday]);

  const renderEventContent = (eventInfo) => (
    <TooltipLight title={eventInfo.event.title} variant="outline">
      <i>{eventInfo.event.title}</i>
    </TooltipLight>
  );
  const handleCompanyHolidayCheckbox = async (e, companyHoliday, index) => {
    setPageLoading(true);
    try {
      const { checked } = e.target;
      // setIsLoading(true);e.target.checked
      const payload = {
        checked,
        key: companyHoliday?.key,
        days: companyHoliday?.days,
        isCustom: companyHoliday?.isCustom,
      };

      const result = await postAPICall(TOGGLE_COMPANY_HOLIDAYS, true, payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );

        const newCompanyHolidaysList = [...companyHolidaysList];
        newCompanyHolidaysList[index].checked = checked;
        setCompanyHolidaysList([...newCompanyHolidaysList]);
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setPageLoading(false);
    }
  };

  const groupDays = (data, isCustom = false) => {
    const grouped = [];

    data.forEach((item) => {
      const lastGroup = grouped[grouped.length - 1];

      if (item?.FK_user_companies_links_id === loggedInUser.userCompanyLinkId) {
        item.UserCompaniesLink.name = "Me";
      }

      if (
        lastGroup &&
        (item?.FK_user_companies_links_id
          ? lastGroup.FK_user_companies_links_id === item.FK_user_companies_links_id
          : lastGroup.name === item.name)
      ) {
        const lastDay = moment(lastGroup.days[lastGroup.days.length - 1].day);
        const currentDay = moment(item.day);
        const diffDays = currentDay.diff(lastDay, "days");

        if (diffDays === 1) {
          lastGroup.days.push({
            day: item.day,
            year: item.year,
          });
        } else {
          grouped.push({
            key: item.key,
            name: item?.name,
            days: [
              {
                day: item.day,
                year: item.year,
              },
            ],
            isCustom,
            checked: !!item?.checked,
            FK_user_companies_links_id: item?.FK_user_companies_links_id,
            UserCompaniesLink: item?.UserCompaniesLink,
          });
        }
      } else {
        grouped.push({
          key: item.key,
          name: item?.name,
          days: [
            {
              day: item.day,
              year: item.year,
            },
          ],
          isCustom,
          checked: !!item?.checked,
          FK_user_companies_links_id: item?.FK_user_companies_links_id,
          UserCompaniesLink: item?.UserCompaniesLink,
        });
      }
    });

    return grouped;
  };

  const fetchCompanyHolidays = async () => {
    setPageLoading(true);
    try {
      const fetchedCompanyHolidays = await getAPICall(
        viewAsUserHoliday ? FETCH_ALL_OUT_OF_OFFICE_DAYS : COMPANY_HOLIDAYS,
        true,
        viewAsUserHoliday ? { includeReportsToUsers: true } : null
      );
      if (fetchedCompanyHolidays?.data?.success) {
        const data = fetchedCompanyHolidays?.data?.data;

        if (viewAsUserHoliday) {
          const convertedData = groupDays(data || [], true);
          setCompanyHolidaysList(convertedData);
        } else {
          const convertedData = [
            ...groupDays(data?.supportedSystemHolidays || []),
            ...groupDays(data?.customHolidays || [], true),
          ];
          setCompanyHolidaysList(convertedData);
        }
      } else {
        setCompanyHolidaysList([]);
        if (fetchedCompanyHolidays.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: fetchedCompanyHolidays.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setPageLoading(false);
    }
  };

  const deleteCompanyHolidays = async (companyHoliday) => {
    setPageLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("key", companyHoliday?.key);
      companyHoliday?.days?.forEach((item) => params.append("days", item?.day));

      if (viewAsUserHoliday)
        params.set(
          "userCompanyLinkId",
          companyHoliday?.FK_user_companies_links_id || loggedInUser.userCompanyLinkId
        );

      const deletedCompanyHolidays = await deleteAPICall(
        viewAsUserHoliday ? DELETE_OUT_OF_OFFICE_DAYS : COMPANY_HOLIDAYS,
        true,
        params
      );
      if (deletedCompanyHolidays?.data?.success) {
        setCompanyHolidaysList([
          ...(companyHolidaysList || []).filter((holiday) => holiday.key !== companyHoliday.key),
        ]);
      } else if (deletedCompanyHolidays.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: deletedCompanyHolidays.response.data.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setPageLoading(false);
    }
  };

  const handleOutOfOfficeDaysModalOpen = () => {
    setIsOutOfOfficeDaysModalOpen(true);
  };

  const handleOutOfOfficeDaysModalClose = (refetchCompanyHolidays = false) => {
    setSelectedDates(null);
    setIsEditing(false);
    setIsOutOfOfficeDaysModalOpen(false);

    if (refetchCompanyHolidays) fetchCompanyHolidays();
  };

  const onSelect = (dateProps) => {
    setSelectedDates({
      startDate: moment(dateProps?.startStr),
      endDate: moment(dateProps?.endStr).subtract(1, "d"),
    });
    handleOutOfOfficeDaysModalOpen();
  };

  const handleEventClick = (clickInfo) => {
    if (!clickInfo?.event?.extendedProps?.isCustom) return;
    setSelectedDates({
      startDate: moment(clickInfo.event.start),
      endDate: moment(clickInfo.event.end || clickInfo.event.start),
      companyHolidayName: clickInfo.event.title,
      key: clickInfo?.event?.extendedProps?.key,
    });
    setIsEditing(true);
    handleOutOfOfficeDaysModalOpen();
  };

  const editCompanyHolidays = async (companyHoliday) => {
    setSelectedDates({
      startDate: moment(companyHoliday.days[0].day),
      endDate: moment(companyHoliday.days[companyHoliday.days.length - 1].day),
      companyHolidayName: companyHoliday.name,
      key: companyHoliday.key,
    });
    setIsEditing(true);
    handleOutOfOfficeDaysModalOpen();
  };

  useEffect(() => {
    fetchCompanyHolidays();
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (calendarRef.current) {
        const height = calendarRef.current.offsetHeight;
        setCalendarHeight(height);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const getConvertedDateString = (days = []) =>
    `${moment(days[0].day).format("dddd, MMMM Do")}${
      days?.length > 1 ? ` - ${moment(days[days.length - 1].day).format("dddd, MMMM Do")}` : ""
    }`;

  return (
    <Grid item xs={12}>
      <Loader loading={pageLoading} />
      <Card>
        <MDBox p={3} pb={0}>
          <MDTypography variant="h4">
            {viewAsUserHoliday ? "Calendar" : "Company Holidays"}
          </MDTypography>
        </MDBox>

        <Grid p={3} container spacing={3}>
          <Grid
            item
            xs={12}
            lg={viewAsUserHoliday ? 7 : 8}
            sx={{ height: { xs: "auto", lg: "fit-content" } }}
            ref={calendarRef}
          >
            {viewAsUserHoliday ? null : (
              <MDBox pb={3}>
                <MDTypography variant="body1">
                  With a company holiday being enabled, users won&apos;t be tracking KPIs that day
                  and won&apos;t be counted against their point averages and streaks.
                </MDTypography>
              </MDBox>
            )}
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={calendarEvents}
              defaultAllDay
              // eslint-disable-next-line react/jsx-no-bind
              eventContent={renderEventContent}
              eventBackgroundColor={theme.palette.primaryRed.main}
              eventBorderColor="transparent"
              eventDisplay="block"
              editable
              selectable
              selectOverlap={false}
              aspectRatio={1.2}
              longPressDelay={500}
              select={onSelect}
              eventClick={handleEventClick}
            />
          </Grid>
          <Grid item xs={12} lg={viewAsUserHoliday ? 5 : 4}>
            <MDBox
              sx={{
                height: { xs: "auto", lg: calendarHeight },
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDTypography variant="h5" ml={2}>
                {viewAsUserHoliday ? "Out of office" : "Company Holidays"}
              </MDTypography>
              <MDBox sx={{ flex: 1, overflowY: "scroll" }} my={3} ml={2} mr={0}>
                {viewAsUserHoliday ? (
                  <CompanyHolidaysUserTable
                    companyHolidaysList={companyHolidaysList}
                    deleteCompanyHolidays={deleteCompanyHolidays}
                  />
                ) : (
                  <>
                    <MDTypography variant="body2">Enabled</MDTypography>
                    {companyHolidaysList.map((companyHoliday, index) => (
                      <Stack
                        direction="row"
                        alignItems="center"
                        my={1}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${companyHoliday.key}-${companyHoliday.days.length}-${index}`}
                      >
                        <Switch
                          checked={companyHoliday?.checked}
                          onChange={(e) => handleCompanyHolidayCheckbox(e, companyHoliday, index)}
                        />
                        <MDBox display="flex" flexDirection="column" ml={1} flex={1}>
                          <MDTypography variant="h6">{companyHoliday?.name}</MDTypography>
                          <MDTypography variant="body2">
                            {getConvertedDateString(companyHoliday?.days)}
                          </MDTypography>
                        </MDBox>
                        {companyHoliday?.isCustom ? (
                          <>
                            <IconButton
                              size="large"
                              color="inherit"
                              variant="contained"
                              onClick={() => editCompanyHolidays(companyHoliday)}
                            >
                              <Icon fontSize="medium">edit</Icon>
                            </IconButton>
                            <IconButton
                              size="large"
                              color="inherit"
                              variant="contained"
                              onClick={() => deleteCompanyHolidays(companyHoliday)}
                            >
                              <Icon fontSize="medium">delete</Icon>
                            </IconButton>
                          </>
                        ) : null}
                      </Stack>
                    ))}
                  </>
                )}
              </MDBox>
              <MDBox display="flex" justifyContent="end">
                <MDButton
                  color="primaryRed"
                  disabled={pageLoading}
                  onClick={handleOutOfOfficeDaysModalOpen}
                >
                  Add New
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {isOutOfOfficeDaysModalOpen ? (
        <SetOutOfOfficeDaysModal
          open={isOutOfOfficeDaysModalOpen}
          handleClose={handleOutOfOfficeDaysModalClose}
          title={
            !viewAsUserHoliday
              ? isEditing
                ? "Edit Company Holiday"
                : "Add New Company Holiday"
              : "Set out of office days"
          }
          description={
            !viewAsUserHoliday
              ? "By adding a new holiday all users in the company will not be tracking KPIs this day."
              : "Add any OOO days you’ve already taken or plan on taking outside of company holidays. If you have users reporting to you, you can also add them."
          }
          view={viewAsUserHoliday ? "team" : "company"}
          values={selectedDates}
          isEditing={isEditing}
        />
      ) : null}
    </Grid>
  );
};

export default CompanyHolidays;
